import { useState } from 'react'
import { GitBranch, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { leadsAPI } from '../api'
import type { Underenhet } from '../api'
import { useLatest } from '../../Bruktbil/useLatest'
import CellTooltip from '../../../components/CellTooltip'

interface Props {
  orgNr: string
  orgNavn?: string | null
}

const PAGE_SIZE = 50

/**
 * The hovedenhet's underenheter from Brreg, sorted on poststed (#1002).
 * The car column counts cars registered on the bedriftsnummer itself; most
 * companies register their cars on the hovedenhet, so the tooltip says so.
 */
export default function UnderenheterSection({ orgNr, orgNavn }: Props) {
  const { t } = useTranslation('leads')
  const [items, setItems] = useState<Underenhet[]>([])
  const [total, setTotal] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [page, setPage] = useState(1)

  useLatest(alive => {
    setLoading(true)
    setError(false)
    leadsAPI.getUnderenheter(orgNr, { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE })
      .then(res => {
        if (!alive()) return
        setItems(res.data.items)
        setTotal(res.data.total)
        setHasMore(res.data.has_more)
      })
      .catch(() => { if (alive()) setError(true) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [orgNr, page])

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          <GitBranch className="w-5 h-5 inline mr-2" />{t('underenheter.title')}
        </h2>
        {!error && !loading && (
          <span className="text-sm text-gray-500 dark:text-gray-400">{t('common.totalCount', { n: total })}</span>
        )}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('underenheter.intro', { navn: orgNavn ?? orgNr })}</p>

      {error ? (
        <p className="text-red-600 dark:text-red-400">{t('underenheter.error')}</p>
      ) : loading ? (
        <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      ) : items.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">{t('underenheter.empty')}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact" data-testid="underenheter-table">
              <thead>
                <tr>
                  <th className="text-left whitespace-nowrap">{t('underenheter.navn')}</th>
                  <th className="text-left whitespace-nowrap">{t('underenheter.bedriftsnummer')}</th>
                  <th className="text-left whitespace-nowrap">{t('underenheter.postnummer')}</th>
                  <th className="text-left whitespace-nowrap">{t('underenheter.poststed')}</th>
                  <th className="text-right whitespace-nowrap">
                    <CellTooltip content={t('underenheter.bilerTooltip')} width={320}>
                      <span className="inline-flex items-center gap-1 cursor-help" data-testid="underenheter-biler-header">
                        {t('underenheter.biler')}
                        <Info className="w-3 h-3" />
                      </span>
                    </CellTooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((u, idx) => (
                  <tr key={u.bedriftsnummer} className={idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}>
                    <td className="text-sm">{u.navn}</td>
                    <td className="font-mono text-xs">{u.bedriftsnummer}</td>
                    <td className="text-xs">{u.postnummer ?? '—'}</td>
                    <td className="text-xs">{u.poststed ?? '—'}</td>
                    <td className="text-sm text-right tabular-nums">{u.car_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pages > 1 || hasMore) && (
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mt-4">
              <span>{t('common.pageOf', { page, pages })}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {t('common.prev')}
                </button>
                <button onClick={() => setPage(p => p + 1)} disabled={!hasMore}
                  className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
